import { EnvironmentInjector, Injectable, inject } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { z } from "zod";
import { AgentKitService } from "../../../agent-ui";
import { MapInteractionService, ScreenshotResult } from "./map-interaction.service";
import { FileUploadService } from "./file-upload.service";
import { FileReaderService } from "../../../viewer-components/services/readers/file-reader.service";
import { SearchQueryService } from "../../../compound-components/services/search";
import type { SearchResult } from "../../../compound-components/model";
import {
  GeometryInputMethod,
  GeometryType,
  MAX_GEOFENCING_AREA_SQUARE_KILOMETERS,
} from "../../../compound-components/model";
import type { ContentBlockRef } from "../model/block.types";
import { AiChatSessionService } from "./ai-chat-session.service";
import { GeoService } from "../../../viewer-components/services/internal-services/geo.service";
import { LayerUtilsService } from "../../../viewer-components/services/internal-services/layer-utils.service";
import { VIEWER_SERVICE } from "../../../viewer-components/services/internal-services/viewer-services/inject-base-viewer.service";
import { GeofencingService } from "../../../shared/services/geofencing.service";

/**
 * Registers all map viewer tools as CopilotKit frontend tools.
 *
 * Provided in ChatContainerComponent (not root) to avoid the NG0200 circular-
 * dependency that surfaces when this service is root-scoped. Tools are added
 * via AgentKitService.addFrontendTool() WITHOUT a DestroyRef cleanup, so they
 * persist in runHandler._tools for the entire app lifetime even when the chat
 * panel is destroyed and recreated (bubble close/open, new conversation, bubble
 * ↔ toolbar switch). CopilotKit's addTool() skips duplicates, so subsequent
 * registrations on panel re-open are no-ops.
 *
 * The injector passed to addFrontendTool is the EnvironmentInjector, not the
 * component injector. This ensures tool handlers remain valid after the
 * originating ChatContainerComponent is destroyed — the environment injector
 * lives for the full app lifetime. The handler closures capture root-scoped
 * service instances (MapInteractionService, SearchQueryService) directly, so
 * they are equally stable.
 */
@Injectable()
export class MapFrontendToolsService {
  private readonly mapInteraction = inject(MapInteractionService);

  private readonly searchQuery = inject(SearchQueryService);

  private readonly agentKit = inject(AgentKitService);

  private readonly fileUpload = inject(FileUploadService);

  private readonly fileReader = inject(FileReaderService);

  private readonly chatSession = inject(AiChatSessionService);

  private readonly viewerService = inject(VIEWER_SERVICE);

  private readonly geofencingService = inject(GeofencingService);

  // EnvironmentInjector is app-lifetime — safe to use after component is destroyed.
  // The component injector would become stale when the chat panel closes.
  private readonly injector = inject(EnvironmentInjector);

  constructor() {
    this.agentKit.addFrontendTool({
      name: "move_to",
      description: "Pan the map to a specific geographic coordinate.",
      parameters: z.object({
        lon: z.number().describe("Longitude in decimal degrees."),
        lat: z.number().describe("Latitude in decimal degrees."),
        zoom: z.number().int().optional().describe("Optional zoom level (1-22)."),
      }),
      handler: async (args) => {
        this.mapInteraction.moveTo(args.lon as number, args.lat as number, args.zoom as number);
        return { moved: true, lon: args.lon, lat: args.lat, zoom: args.zoom ?? null };
      },
      injector: this.injector,
    });

    this.agentKit.addFrontendTool({
      name: "set_zoom",
      description: "Set the map zoom level without changing the center position.",
      parameters: z.object({
        zoom: z.number().int().describe("Zoom level between 1 (world view) and 22 (building detail)."),
      }),
      handler: async (args) => {
        this.mapInteraction.setZoom(args.zoom as number);
        return { zoom_set: true, zoom: args.zoom };
      },
      injector: this.injector,
    });

    this.agentKit.addFrontendTool({
      name: "add_data",
      description: "Add a new data layer to the map viewer.",
      parameters: z.object({
        url: z.string().describe("URL of the data source."),
        layer_name: z.string().describe("Human-readable name to identify the layer."),
        layer_type: z.enum(["wms", "geojson", "wmts", "xyz"]).default("wms").describe("Type of the data source."),
      }),
      handler: async (args) => {
        const layerTypeMap: Record<string, "WMS" | "GeoJSON" | "WMTS" | "XYZ"> = {
          wms: "WMS",
          geojson: "GeoJSON",
          wmts: "WMTS",
          xyz: "XYZ",
        };
        const layerType = layerTypeMap[args.layer_type as string] ?? "WMS";
        this.mapInteraction.addData(args.url as string, layerType, args.layer_name as string);
        return { layer_added: true, url: args.url, layer_name: args.layer_name, layer_type: args.layer_type };
      },
      injector: this.injector,
    });

    this.agentKit.addFrontendTool({
      name: "zoom_to_extent",
      description: "Fit the map view to a geographic bounding box.",
      parameters: z.object({
        min_lon: z.number().describe("Western boundary longitude."),
        min_lat: z.number().describe("Southern boundary latitude."),
        max_lon: z.number().describe("Eastern boundary longitude."),
        max_lat: z.number().describe("Northern boundary latitude."),
      }),
      handler: async (args) => {
        this.mapInteraction.zoomToExtent(
          args.min_lon as number,
          args.min_lat as number,
          args.max_lon as number,
          args.max_lat as number,
        );
        return { zoomed: true, bbox: [args.min_lon, args.min_lat, args.max_lon, args.max_lat] };
      },
      injector: this.injector,
    });

    this.agentKit.addFrontendTool({
      name: "get_layer_geometry",
      description:
        "Get the geometry of a map layer by its stable id (from overlay_layers in map_status — works for a top-level layer or a child of a layer group, at any nesting depth). The geometry is uploaded to the file store and only a file reference (file_id, url) is returned — the raw GeoJSON never travels through the chat itself.",
      parameters: z.object({
        layer_id: z
          .string()
          .describe(
            "Stable id of the layer, exactly as listed under overlay_layers in the current map_status. Never guess, construct, or pass a display name instead.",
          ),
      }),
      handler: async (args) => {
        const layerId = args.layer_id as string;
        const layer = this.viewerService.findUserLayerById(layerId);
        if (!layer) {
          return { error: `Layer not found: ${layerId}`, layer_id: layerId };
        }
        const result = this.mapInteraction.getLayerGeometry(layerId);
        if (!result) {
          const error = `Layer "${layer.name}" (id=${layerId}) matched but its geometry could not be resolved.`;
          console.error("[get_layer_geometry]", error);
          return { error, layer_id: layerId, available_layers: this.listAvailableLayers() };
        }
        console.debug("[get_layer_geometry] resolved", {
          layer_id: layerId,
          layer_name: layer.name,
          has_style: !!layer.styleDefinition,
          style: layer.styleDefinition,
          style_metadata: layer.styleMetadata,
        });
        // Upload the geometry instead of inlining it — same {file_id, url} pattern as
        // get_screenshot. The raw GeoJSON never sits in the ToolMessage/chat history,
        // which would otherwise bloat every subsequent request for large layers.
        // style/style_metadata (ARCH_map_tools.md §13.1) travel embedded as extra
        // top-level keys ON the FeatureCollection itself — the backend reads them
        // back from the uploaded file content, not from the tool result, so they
        // must NOT be sibling fields here (they'd be lost, only file_id/url reach it).
        const geojsonWithStyle = {
          ...result.geojson,
          ...(layer.styleDefinition ? { style: layer.styleDefinition } : {}),
          ...(layer.styleMetadata ? { style_metadata: layer.styleMetadata } : {}),
        };
        try {
          const threadId = this.chatSession.threadId();
          const file = new File([JSON.stringify(geojsonWithStyle)], `${layer.name}.geojson`, {
            type: "application/geo+json",
          });
          const uploaded = await firstValueFrom(this.fileUpload.upload(file, threadId));
          return {
            file_id: uploaded.id,
            url: uploaded.url,
            layer_id: layerId,
          };
        } catch (err) {
          console.error("[get_layer_geometry] upload failed", layerId, err);
          return {
            error: "Layer geometry upload failed",
            layer_id: layerId,
            available_layers: this.listAvailableLayers(),
          };
        }
      },
      injector: this.injector,
    });

    this.agentKit.addFrontendTool({
      name: "extract_embeddings",
      description:
        "Search for relevant data layers or geographic datasets using semantic similarity. " +
        "Requires a geofencing area: either pass `geojson`, or have the user configure one in the search bar first. " +
        `The area must stay below ${MAX_GEOFENCING_AREA_SQUARE_KILOMETERS.toLocaleString("en-US")} km².`,
      parameters: z.object({
        query: z.string().describe("Natural language description of the data to search for."),
        top_k: z.number().int().optional().describe("Maximum number of results to return (default: 5)."),
        collection: z.string().optional().describe("Optional collection/namespace to search within."),
        geojson: z
          .record(z.any())
          .optional()
          .nullable()
          .describe(
            "GeoJSON geometry constraining the search area. Required unless a geofencing area is already " +
              `applied. Must cover less than ${MAX_GEOFENCING_AREA_SQUARE_KILOMETERS.toLocaleString("en-US")} km².`,
          ),
      }),
      handler: async (args) => {
        if (args.geojson) {
          try {
            const geojson = args.geojson as any;
            // GeoService.geoJSONFeatureToWKT only accepts a single Feature/Geometry — a
            // FeatureCollection (e.g. forwarded from get_layer_geometry) needs the multi-feature path.
            const wkt =
              geojson.type === "FeatureCollection"
                ? GeoService.geoJSONFeaturesToWkt(geojson.features ?? [], 4326, 4326)
                : GeoService.geoJSONFeatureToWKT(geojson);
            if (wkt) {
              this.searchQuery.updateSearchFilters({ wkt });
              // Geofencing layer only supports polygon/circle geometry — skip points
              // and lines, they'd have no area to render as a geofencing shape.
              if (/^(MULTI)?POLYGON/i.test(wkt.trim())) {
                this.geofencingService.requestCreateOrUpdateLayer({
                  success: true,
                  inputMethod: GeometryInputMethod.MAP,
                  type: GeometryType.POLYGON,
                  geometryType: "polygon",
                  wkt,
                  displayWkt: wkt,
                });
              }
            }
          } catch (err) {
            console.error("extract_embeddings: failed to convert geojson to WKT, ignoring spatial filter", err);
          }
        }

        // The embeddings API needs a bounded geofencing area. SearchQueryService rejects the
        // search silently in that case (returning null), which would look like "no results" —
        // so check the precondition here and return an actionable error instead.
        const geofencingValidation = this.searchQuery.getCurrentGeofencingValidation();
        if (!geofencingValidation.valid) {
          console.error("extract_embeddings: blocked by geofencing precondition:", geofencingValidation.reason);
          return {
            success: false,
            error_type: geofencingValidation.area?.exceedsLimit
              ? "geofencing_area_too_large"
              : "geofencing_required",
            error: `${geofencingValidation.reason} Pass a smaller \`geojson\` area, or ask the user to configure geofencing in the search bar. Do not report success to the user.`,
          };
        }

        // SearchQueryService.executeSearch() catches HTTP errors internally (to keep
        // the search UI working) and returns EMPTY instead of propagating the error.
        // firstValueFrom(EMPTY) throws EmptyError — we catch it and read the real
        // failure details from the service state, which was already updated.
        let result: SearchResult | null = null;
        try {
          result = await firstValueFrom(this.searchQuery.performTextSearch(args.query as string));
        } catch {
          const state = this.searchQuery.getCurrentSearchState();
          const errorDetail = state.error ?? "Search service unavailable";
          console.error("extract_embeddings: search returned EMPTY (HTTP error):", state.errorType, errorDetail);
          return {
            success: false,
            error_type: state.errorType ?? "unknown",
            error: `Search failed for query "${args.query}": ${errorDetail}. The search did not complete — do not report success to the user.`,
          };
        }

        const count = result?.items?.length ?? 0;
        return {
          success: true,
          count,
          summary: `Found ${count} datasets matching "${args.query}"`,
        };
      },
      injector: this.injector,
    });

    this.agentKit.addFrontendTool({
      name: "get_screenshot",
      description: "Capture the current map view as a screenshot image for visual analysis.",
      parameters: z.object({}),
      handler: async (_args) => {
        let shot: ScreenshotResult;
        try {
          shot = await this.mapInteraction.captureScreenshot();
        } catch (err) {
          console.error("[MapFrontendTools] get_screenshot: canvas capture failed", err);
          return { success: false, error: String(err) };
        }
        try {
          const threadId = this.chatSession.threadId();
          const file = new File([shot.blob], "screenshot.png", { type: "image/png" });
          const result = await firstValueFrom(this.fileUpload.upload(file, threadId));
          return { success: true, file_id: result.id, url: result.url, bbox: shot.bbox, zoom: shot.zoom };
        } catch (err) {
          console.error("[MapFrontendTools] get_screenshot: upload failed", err);
          return { success: false, error: "Screenshot upload failed" };
        }
      },
      injector: this.injector,
    });

    this.agentKit.addFrontendTool({
      name: "add_geojson_layer",
      description:
        "Fetch a GeoJSON dataset from a file URL (or accept inline GeoJSON) and add it as a named user layer on the map. Prefer file_url when a file reference is available.",
      parameters: z.object({
        layer_name: z.string().describe("Human-readable display name for the new layer."),
        file_url: z
          .string()
          .optional()
          .describe("File-store URL of a GeoJSON file to fetch and add. Preferred over geojson_string."),
        geojson_string: z
          .string()
          .optional()
          .describe("Inline GeoJSON as a JSON string. Use only when no file URL is available."),
      }),
      handler: async (args) => {
        const name = (args.layer_name as string) || "AI Layer";
        let rawGeojson: unknown = null;

        if (args.file_url) {
          try {
            const ref = { filename: `${name}.geojson`, size_bytes: null } as Pick<ContentBlockRef, "filename" | "size_bytes">;
            const result = await firstValueFrom(
              this.fileUpload.fetchFileContent(args.file_url as string, "application/geo+json", ref),
            );
            rawGeojson = result.geojson;
          } catch (err: any) {
            console.error("[add_geojson_layer] fetch failed", args.file_url, err);
            return { success: false, error: `Failed to fetch GeoJSON from URL: ${err?.message ?? err}` };
          }
        } else if (args.geojson_string) {
          try {
            rawGeojson = JSON.parse(args.geojson_string as string);
          } catch {
            return { success: false, error: "Invalid GeoJSON string — JSON.parse failed." };
          }
        }

        if (!rawGeojson) {
          return { success: false, error: "Provide file_url or geojson_string." };
        }

        const prepared = this.fileReader.prepareGeoJson(rawGeojson, `${name}.geojson`);
        if (!prepared.data) {
          console.error("[add_geojson_layer] normalization failed", prepared.errors);
          return {
            success: false,
            error: "GeoJSON normalization produced no valid features.",
            details: prepared.errors,
          };
        }

        this.mapInteraction.addGeoJsonLayer(prepared.data);
        return { success: true, layer_name: name, feature_count: prepared.data.features.length };
      },
      injector: this.injector,
    });

    this.agentKit.addFrontendTool({
      name: "apply_layer_style",
      description:
        "Acknowledge receipt of a backend-generated map-layer style request. This tool does not modify the map: " +
        "the backend derives the styled map-layer evidence from the receipt, and the user decides whether to add " +
        "or update the layer through the map-layer card.",
      parameters: z.object({
        layer_id: z.string().describe("Stable id of the layer to restyle, matching overlay_layers[].id."),
        style: z.record(z.any()).describe("Style contract: { type: 'flat', properties } or { type: 'rules', rules }."),
        metadata: z
          .record(z.any())
          .optional()
          .describe("Style generation metadata (visualization_type, legend, etc.)."),
      }),
      handler: async (args) => {
        console.debug("[apply_layer_style] received", {
          layer_id: args.layer_id,
          style: args.style,
          metadata: args.metadata,
        });

        // Ack-only receipt (S7): the handler resumes the agent WITHOUT mutating the map.
        // No layer lookup, no style application, no legend, no toast — the backend derives
        // the styled map_layer evidence from this receipt, and the user decides add/update
        // through the map-layer card. Never apply a style manually here.
        const result = { success: true, layer_id: args.layer_id };
        console.debug("[apply_layer_style] acked", result);
        return result;
      },
      injector: this.injector,
    });
  }

  /** Lists every layer currently on the map (including group children at any depth) as {id, name} pairs. */
  private listAvailableLayers(): { id: string; name: string }[] {
    return LayerUtilsService.flattenWithChildren(this.viewerService.getUserLayers()).map((l) => ({
      id: l.id,
      name: l.name,
    }));
  }
}
